import { useState, useEffect, useCallback } from 'react'
import { customerApi } from '../services/api'
import AgentCard from '../components/agents/AgentCard'
import AgentPriorityList from '../components/agents/AgentPriorityList'
import AgentSettingsForm from '../components/agents/AgentSettingsForm'
import { CheckIcon } from '@heroicons/react/24/outline'

export default function Agents({ onNavigate }) {
  const [agents, setAgents] = useState([])
  const [priority, setPriority] = useState([])
  const [expandedAgent, setExpandedAgent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [savingPriority, setSavingPriority] = useState(false)
  const [prioritySaved, setPrioritySaved] = useState(false)

  const fetchAgents = useCallback(async () => {
    try {
      const res = await customerApi.get('/customer/agents/')
      setAgents(res.data.agents || [])
      setPriority(res.data.routing_priority || [])
    } catch (err) {
      console.error('Failed to load agents:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAgents()
  }, [fetchAgents])

  const handleToggle = async (slug, enabled) => {
    try {
      const endpoint = enabled ? 'enable' : 'disable'
      await customerApi.put(`/customer/agents/${slug}/${endpoint}`)
      // Optimistic update
      setAgents((prev) =>
        prev.map((a) => (a.slug === slug ? { ...a, enabled } : a))
      )
    } catch (err) {
      console.error(`Failed to ${enabled ? 'enable' : 'disable'} agent:`, err)
      fetchAgents() // Revert on error
    }
  }

  const handleSavePriority = async (newPriority) => {
    setPriority(newPriority)
    setSavingPriority(true)
    setPrioritySaved(false)
    try {
      await customerApi.put('/customer/agents/priority', { routing_priority: newPriority })
      setPrioritySaved(true)
      setTimeout(() => setPrioritySaved(false), 2000)
    } catch (err) {
      console.error('Failed to save priority:', err)
      fetchAgents() // Revert
    } finally {
      setSavingPriority(false)
    }
  }

  const handleSaveSettings = async (slug, settings) => {
    await customerApi.put(`/customer/agents/${slug}/settings`, { settings })
    fetchAgents()
  }

  const handleExpand = (slug) => {
    setExpandedAgent(expandedAgent === slug ? null : slug)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-slate-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900">AI Agents</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Configure your multi-agent system. Agents are checked in priority order — the first match handles the query.
        </p>
      </div>

      {/* Routing Priority */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Routing Priority</h3>
            <p className="text-xs text-slate-500">Drag agents to change the order they are checked</p>
          </div>
          {prioritySaved && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckIcon className="h-3.5 w-3.5" />
              Saved
            </span>
          )}
        </div>
        <AgentPriorityList priority={priority} onChange={handleSavePriority} />
      </div>

      {/* Agent Cards */}
      <div className="space-y-3">
        {agents.map((agent) => (
          <AgentCard
            key={agent.slug}
            agent={agent}
            onToggle={handleToggle}
            onNavigate={onNavigate}
            expanded={expandedAgent === agent.slug}
            onExpand={handleExpand}
          />
        ))}
      </div>

      {/* Expanded Agent Settings Form */}
      {expandedAgent && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">
            {agents.find((a) => a.slug === expandedAgent)?.name} — Advanced Settings
          </h3>
          {agents.find((a) => a.slug === expandedAgent) && (
            <AgentSettingsForm
              agent={agents.find((a) => a.slug === expandedAgent)}
              onSave={handleSaveSettings}
            />
          )}
        </div>
      )}
    </div>
  )
}
